import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';

const CsvImportContext = createContext();

export const useCsvImportContext = () => {
  const context = useContext(CsvImportContext);
  if (!context) {
    throw new Error('useCsvImportContext must be used within a CsvImportProvider');
  }
  return context;
};

export const CsvImportProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);

  // Use the absolute URL with port 8000 for development
  const baseURL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:8000/api/csv-import'
    : '/api/csv-import';

  // Get the authentication token from localStorage
  const token = localStorage.getItem('token');

  const api = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': token ? `Token ${token}` : '',
    },
    withCredentials: true,
  });

  const handleApiError = (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred';
    throw new Error(message);
  };

  const uploadCsvFile = async (formData) => {
    try {
      setIsLoading(true);
      // Use the full URL for the upload endpoint to avoid path issues
      const uploadUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:8000/api/csv-import/upload-csv/'
        : '/api/csv-import/upload-csv/';

      console.log('Uploading to:', uploadUrl);

      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Token ${token}` : '',
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error('Upload error:', error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCsvInfo = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/${sessionId}/get-csv-info/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const suggestFieldMappings = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/${sessionId}/suggest-field-mappings/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateFieldMappings = async (sessionId, mappings) => {
    try {
      setIsLoading(true);
      const response = await api.post(`/${sessionId}/update-field-mappings/`, {
        mappings,
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateCsvData = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.post(`/${sessionId}/validate-csv-data/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const correctValidationErrors = async (sessionId, corrections) => {
    try {
      setIsLoading(true);
      const response = await api.post(`/${sessionId}/correct-validation-errors/`, {
        corrections,
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const detectDuplicates = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.post(`/${sessionId}/detect-duplicates/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveDuplicates = async (sessionId, resolutions) => {
    try {
      setIsLoading(true);
      const response = await api.post(`/${sessionId}/resolve-duplicates/`, {
        resolutions,
      });
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPreviewData = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/${sessionId}/get-preview-data/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const importCsvData = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.post(`/${sessionId}/import-csv-data/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getImportProgress = async (sessionId) => {
    try {
      const response = await api.get(`/${sessionId}/get-import-progress/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  };

  const cancelImport = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.post(`/${sessionId}/cancel-import/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getImportSummary = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/${sessionId}/get-import-summary/`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadErrorReport = async (sessionId) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/${sessionId}/download-error-report/`, {
        responseType: 'blob',
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `import-errors-${sessionId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSystemFields = async (entityType) => {
    try {
      setIsLoading(true);
      const response = await api.get(`/system-fields/?entity_type=${entityType}`);
      return response.data;
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    isLoading,
    uploadCsvFile,
    getCsvInfo,
    suggestFieldMappings,
    updateFieldMappings,
    validateCsvData,
    correctValidationErrors,
    detectDuplicates,
    resolveDuplicates,
    getPreviewData,
    importCsvData,
    getImportProgress,
    cancelImport,
    getImportSummary,
    downloadErrorReport,
    getSystemFields,
  };

  return (
    <CsvImportContext.Provider value={value}>
      {children}
    </CsvImportContext.Provider>
  );
};

export default CsvImportContext;
